import { useCallback, useEffect, useState } from 'react';
import { FeatureGate, useFeatureGate } from '@featureFlags/index';
import { setFeatureGateContext } from '@utils/datadog';
import {
  ASSISTANT_PANEL_WIDTH,
  ASSISTANT_RAIL_WIDTH,
  readAssistantOpen,
  writeAssistantOpen,
} from './processAssistant.constants';

export interface AssistantPanelState {
  /** Whether the panel may be shown at all on this surface. */
  readonly available: boolean;
  readonly open: boolean;
  /**
   * Whether the chat should be rendered, open or not. Stays true once the
   * panel has been open for this process, until the user navigates away: a
   * turn outlives a collapse, and unmounting the chat would drop the report
   * that keeps the canvas frozen and the refresh that follows the turn.
   */
  readonly chatMounted: boolean;
  readonly toggle: () => void;
  /** Horizontal space the canvas must leave for the panel, 0 when absent. */
  readonly width: number;
}

interface PanelState {
  processId: string | undefined;
  open: boolean;
  chatMounted: boolean;
}

const initialState = (processId: string | undefined): PanelState => {
  const open = readAssistantOpen(processId ?? '');
  return { processId, open, chatMounted: open };
};

/**
 * Whether the assistant is offered here, and whether its panel is open.
 *
 * Owned by the editor page rather than the panel because the canvas has to
 * reserve the panel's width. The assistant edits the draft through the same
 * API the user does, so it is offered on exactly the surface the user could
 * edit by hand — never a published process.
 */
export const useAssistantPanelState = (
  processId: string | undefined,
  isPublished: boolean,
): AssistantPanelState => {
  const gateEnabled = useFeatureGate(FeatureGate.PROCESS_ASSISTANT);
  useEffect(() => {
    // Exposed on every RUM event so the A/B cohorts can be split on it.
    setFeatureGateContext(FeatureGate.PROCESS_ASSISTANT, gateEnabled);
  }, [gateEnabled]);

  const [state, setState] = useState(() => initialState(processId));

  // Navigating to another process re-reads its own preference. Adjusted during
  // render rather than in an effect so the canvas never reserves the wrong
  // width for a frame — and it drops ``chatMounted`` with it, so the new
  // process doesn't inherit a mounted chat it was never opened for.
  if (state.processId !== processId) {
    setState(initialState(processId));
  }

  const toggle = useCallback(() => {
    setState((prev) => {
      const open = !prev.open;
      writeAssistantOpen(processId ?? '', open);
      return { processId, open, chatMounted: prev.chatMounted || open };
    });
  }, [processId]);

  const available = gateEnabled && !isPublished && Boolean(processId);
  const openWidth = state.open ? ASSISTANT_PANEL_WIDTH : ASSISTANT_RAIL_WIDTH;

  return {
    available,
    open: state.open,
    chatMounted: available && state.chatMounted,
    toggle,
    width: available ? openWidth : 0,
  };
};
